import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Award, UserPlus, KeyRound, Copy, Trash2, Pencil, Eye, X, Send, Users,
  CheckCircle2, LogIn, Lightbulb, MessageSquareHeart, Ban, RotateCcw, Link2,
  Share2, MessageCircle, Smartphone, Mail, MailCheck, MailWarning, MousePointerClick, AlertTriangle, Eye as EyeIcon, Scale, Download, Upload, Search, FileText, ChevronDown, MailPlus, MailOpen, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErr, adminDelete, adminPutProtected } from "../../lib/api";
import { NavigationAnalysis } from "../../components/NavigationAnalysis";

const STEP_DEFS = [
  { key: "invited", label: "Invité", icon: Send },
  { key: "registered", label: "Inscrit", icon: CheckCircle2 },
  { key: "logged_in", label: "Connecté", icon: LogIn },
  { key: "idea_submitted", label: "Idée déposée", icon: Lightbulb },
  { key: "feedback", label: "Retour donné", icon: MessageSquareHeart },
];

export default function AdminAmbassadors() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [journey, setJourney] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const [bulkInvite, setBulkInvite] = useState(null); // { step, eligible }
  const [mailOpen, setMailOpen] = useState(false);
  const [mailAll, setMailAll] = useState(null); // { step, kind } — e-mail groupé à tous
  const [mails, setMails] = useState(null); // analyse des retours, e-mail par e-mail
  const [mailSummary, setMailSummary] = useState(null); // comportement par ambassadeur
  const [mailsView, setMailsView] = useState("mails"); // "mails" | "ambassadeurs"
  const [mailJob, setMailJob] = useState(null); // progression de l'expédition en cours
  const [mailPanel, setMailPanel] = useState(null);
  const [reportFilter, setReportFilter] = useState(""); // tout fermé par défaut
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const isNew = (it) => it.used_at && (Date.now() - new Date(it.used_at).getTime()) < 48 * 3600 * 1000;
  const sortedList = sortBy ? [...list].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0)) : list;

  const load = useCallback(() => {
    api.get("/admin/ambassadors").then((r) => setList(r.data)).catch(() => {});
    api.get("/admin/ambassadors/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Analyse des retours, e-mail par e-mail (chargée directement — section toujours visible).
  const loadMails = useCallback(() => {
    api.get("/admin/ambassadors/mails").then((r) => setMails(r.data)).catch(() => {});
    api.get("/admin/ambassadors/mails/summary").then((r) => setMailSummary(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadMails(); }, [loadMails]);

  // Progression de l'expédition : rechargée au chargement, puis sondée tant qu'un envoi est en cours.
  const loadMailJob = useCallback(() => {
    api.get("/admin/ambassadors/mail-jobs").then((r) => setMailJob(r.data[0] || null)).catch(() => {});
  }, []);
  useEffect(() => { loadMailJob(); }, [loadMailJob]);
  useEffect(() => {
    if (mailJob?.status !== "running") return;
    const iv = setInterval(() => {
      api.get("/admin/ambassadors/mail-jobs").then((r) => {
        const j = r.data[0] || null;
        setMailJob(j);
        if (j?.status === "done") {
          toast.success(`Expédition terminée : ${j.sent}/${j.total} e-mail(s) envoyé(s)${j.failed ? `, ${j.failed} en échec (noté sur les fiches)` : ""}.`, { duration: 8000 });
          setMails(null);
        }
      }).catch(() => {});
    }, 2000);
    return () => clearInterval(iv);
  }, [mailJob?.status]);

  const baseUrl = window.location.origin;
  const inviteLink = (code) => `${baseUrl}/api/ambassador/share/${code}`;
  const copy = (text, msg) => { navigator.clipboard.writeText(text); toast.success(msg); };

  const sendInvite = async (it) => {
    if (!it.invite_email) return toast.error("Aucun e-mail renseigné pour cet ambassadeur.");
    try {
      const { data } = await api.post(`/admin/ambassadors/${it.id}/invite`, { base_url: baseUrl });
      if (!data.smtp_configured) toast.warning("SMTP non configuré : invitation journalisée mais non envoyée. Configurez « Réglages e-mail ».", { duration: 8000 });
      else if (data.status === "envoye") toast.success("Invitation envoyée par e-mail.");
      else toast.error(`Échec de l'envoi : ${data.status}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const eligibleBulk = () => list.filter((it) => it.invite_email && it.status === "active");
  const doBulkInvite = async (ids) => {
    try {
      const payload = { base_url: baseUrl };
      if (ids && ids.length) payload.ids = ids;
      const { data } = await api.post("/admin/ambassadors/invite-bulk", payload);
      if (!data.smtp_configured) toast.warning(`SMTP non configuré : ${data.total} invitation(s) journalisée(s) mais non envoyée(s).`, { duration: 9000 });
      else toast.success(`${data.sent}/${data.total} invitation(s) envoyée(s).`);
      setBulkInvite(null); load();
    } catch (e) { toast.error(apiErr(e)); setBulkInvite(null); }
  };
  const remindUnopened = async () => {
    if (!window.confirm("Envoyer une relance douce aux invités qui ont reçu l'invitation mais ne l'ont pas encore ouverte ?")) return;
    try {
      const { data } = await api.post("/admin/ambassadors/remind", { base_url: baseUrl });
      if (data.total === 0) toast.info("Aucun invité à relancer (tous ont ouvert leur lien ou aucun n'a encore été invité).");
      else if (!data.smtp_configured) toast.warning(`SMTP non configuré : ${data.total} relance(s) journalisée(s).`, { duration: 8000 });
      else toast.success(`${data.sent}/${data.total} relance(s) envoyée(s).`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const leaderboard = [...list]
    .map((it) => ({ ...it, score: (it.page_views || 0) + (it.ideas_count || 0) * 3 + (it.feedback_count || 0) * 2 }))
    .filter((it) => it.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette invitation ambassadeur ?")) return;
    try { if (!await adminDelete(`/admin/ambassadors/${id}`)) return; toast.success("Invitation supprimée."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleStatus = async (it) => {
    try {
      await api.put(`/admin/ambassadors/${it.id}`, { status: it.status === "active" ? "revoked" : "active" });
      toast.success(it.status === "active" ? "Code révoqué." : "Code réactivé.");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleCommittee = async (it) => {
    try {
      if (!await adminPutProtected(`/admin/ambassadors/${it.id}/committee`, { committee: !it.committee })) return;
      toast.success(it.committee ? "Retiré du comité de décision." : "Intégré au comité de décision.");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleEmailInvalid = async (it) => {
    try {
      await api.put(`/admin/ambassadors/${it.id}`, { email_invalid: !it.email_invalid });
      toast.success(it.email_invalid ? "E-mail de nouveau considéré comme valide." : "E-mail marqué comme refusé (retour/bounce) — exclu des prochains envois.");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const cards = stats ? [
    { key: "total", l: "Invités", v: stats.total, icon: Users, c: "#00F0FF", match: () => true },
    { key: "invited", l: "Invitations envoyées", v: stats.invited ?? 0, icon: MailCheck, c: "#64D2FF", match: (it) => !!it.invite_sent_at },
    { key: "opened", l: "Liens ouverts", v: stats.opened ?? 0, icon: MousePointerClick, c: "#FF9F0A", match: (it) => (it.invite_open_count || 0) > 0 },
    { key: "registered", l: "Inscrits", v: stats.registered, icon: CheckCircle2, c: "#30D158", match: (it) => it.registered },
    { key: "logged_in", l: "Connectés", v: stats.logged_in, icon: LogIn, c: "#0A84FF", match: (it) => (it.login_count || 0) > 0 || !!it.last_login_at },
    { key: "page_views", l: "Pages consultées", v: stats.page_views ?? 0, icon: EyeIcon, c: "#5E5CE6", match: (it) => (it.page_views || 0) > 0 },
    { key: "with_ideas", l: "Ont déposé une idée", v: stats.with_ideas, icon: Lightbulb, c: "#FFD60A", match: (it) => (it.ideas_count || 0) > 0 },
    { key: "feedbacks", l: "Retours reçus", v: stats.feedbacks, icon: MessageSquareHeart, c: "#BF5AF2", match: (it) => (it.feedback_count || 0) > 0 },
    { key: "revoked", l: "Codes révoqués", v: stats.revoked, icon: Ban, c: "#FF453A", match: (it) => it.status === "revoked" },
    { key: "email_invalid", l: "E-mails refusés", v: stats.email_invalid ?? 0, icon: MailWarning, c: "#FF6B6B", match: (it) => !!it.email_invalid },
  ] : [];

  const activeCard = reportFilter ? cards.find((c) => c.key === reportFilter) : null;
  const countries = [...new Set(list.map((it) => it.country).filter(Boolean))].sort();
  const fiches = (activeCard ? list.filter(activeCard.match) : []).filter((it) => {
    const q = search.trim().toLowerCase();
    const name = `${it.first_name || ""} ${it.last_name || ""} ${it.invite_name || ""} ${it.email || ""} ${it.invite_email || ""} ${it.code || ""}`.toLowerCase();
    return (!q || name.includes(q)) && (!country || it.country === country);
  });

  const exportAmbassadorsCsv = () => {
    const rows = [["Nom", "E-mail", "Code", "Pays", "Statut", "Permanent", "Comité", "Invité", "Ouvertures", "Inscrit", "Connexions", "Pages", "Idées", "Retours"]];
    list.forEach((it) => rows.push([
      (it.first_name || it.last_name) ? `${it.first_name || ""} ${it.last_name || ""}`.trim() : (it.invite_name || ""),
      it.email || it.invite_email || "", it.code, it.country || "", it.status,
      it.permanent ? "oui" : "non", it.committee ? "oui" : "non", it.invite_sent_at ? "oui" : "non",
      it.invite_open_count || 0, it.registered ? "oui" : "non", it.login_count || 0,
      it.page_views || 0, it.ideas_count || 0, it.feedback_count || 0,
    ]));
    const csv = rows.map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = "ambassadeurs-aca.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Export CSV téléchargé.");
  };

  const shareMessage = (it) => {
    const name = it.invite_name ? `${it.invite_name.split(" ")[0]}, ` : "";
    return `Bonjour ${name}vous êtes invité(e) en avant-première au Comité des Ambassadeurs de APP CHALLENGE ACADEMY (accès privé et exclusif).\n\nVotre code personnel : ${it.code}\nActivez votre accès ici : ${inviteLink(it.code)}\n\nCe lien vous est strictement réservé, merci de ne pas le partager.`;
  };

  return (
    <div className="mx-auto max-w-6xl">
      {/* En-tête */}
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-surface p-5 sm:p-6">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-cyan/20 blur-[100px]" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-cyan"><Award size={14} /> Comité privé</div>
            <h1 className="mt-2 font-display text-2xl font-900 text-white">Ambassadeurs académiques</h1>
            <p className="mt-2 max-w-2xl text-xs leading-relaxed text-ink-secondary">
              Invitez avec un code unique, suivez le parcours (invitation → inscription → connexion → idée → retour) et déroulez le compte rendu global ci-dessous.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setCreateOpen(true)} data-testid="amb-create-open" className="aca-btn-primary">
              <KeyRound size={14} /> Générer un code
            </button>
            <button onClick={() => setManualOpen(true)} data-testid="amb-manual-open" className="aca-btn-secondary">
              <UserPlus size={14} /> Ajouter manuellement
            </button>
            <button onClick={() => setImportOpen(true)} data-testid="amb-import-open" className="aca-btn-secondary">
              <Upload size={14} /> Importer CSV
            </button>
            <button onClick={() => setBulkInvite({ step: 1, eligible: eligibleBulk() })} data-testid="amb-bulk-invite-open" className="aca-btn-secondary">
              <Mail size={14} /> Inviter par e-mail
            </button>
            <button onClick={() => setMailOpen(true)} data-testid="amb-mail-templates-open" className="aca-btn-secondary">
              <MailCheck size={14} /> Mails
            </button>
            <button onClick={remindUnopened} data-testid="amb-remind-open" className="aca-btn-secondary">
              <MousePointerClick size={14} /> Relancer les non-ouverts
            </button>
            <button onClick={() => setMailAll({ step: 1, kind: "bulk" })} data-testid="amb-mail-all-open" className="aca-btn-secondary">
              <MailPlus size={14} /> Envoyer un e-mail à tous
            </button>
            <button onClick={() => setMailAll({ step: 1, kind: "relance_1" })} data-testid="amb-relance-1-open" className="aca-btn-secondary">
              <RotateCcw size={14} /> Relance 1
            </button>
            <button onClick={() => setMailAll({ step: 1, kind: "relance_2" })} data-testid="amb-relance-2-open" className="aca-btn-secondary">
              <RotateCcw size={14} /> Relance 2
            </button>
            <button onClick={() => setMailAll({ step: 1, kind: "relance_3" })} data-testid="amb-relance-3-open" className="aca-btn-secondary">
              <RotateCcw size={14} /> Relance 3
            </button>
            <button onClick={() => setMailAll({ step: 1, kind: "relance_4" })} data-testid="amb-relance-4-open" className="aca-btn-secondary">
              <RotateCcw size={14} /> Relance 4
            </button>
            <button onClick={exportAmbassadorsCsv} data-testid="amb-export-csv" className="aca-btn-outline">
              <Download size={14} /> Exporter (CSV)
            </button>
          </div>
        </div>

        {/* Ligne de progression d'expédition des e-mails (gauche -> droite, en %) */}
        {mailJob && (() => {
          const pct = mailJob.total ? Math.round((mailJob.done / mailJob.total) * 100) : 0;
          return (
            <div className="relative mt-4" data-testid="amb-mail-progress">
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2 text-xs text-ink-secondary">
                <span className="flex items-center gap-2">
                  {mailJob.status === "running" && <Loader2 size={13} className="animate-spin text-cyan" />}
                  {mailJob.status === "running" ? "Expédition en cours…" : "Dernière expédition terminée"} — {mailJob.subject}
                </span>
                <span className="tabular-nums text-cyan" data-testid="amb-mail-progress-pct">
                  {pct}% · {mailJob.sent}/{mailJob.total} envoyé(s){mailJob.failed ? ` · ${mailJob.failed} échec(s)` : ""}{mailJob.skipped ? ` · ${mailJob.skipped} exclu(s)` : ""}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
                <div className={`h-full rounded-full transition-all duration-500 ease-out ${mailJob.status === "running" ? "bg-cyan" : "bg-green-400"}`}
                  style={{ width: `${pct}%` }} data-testid="amb-mail-progress-bar" />
              </div>
            </div>
          );
        })()}
      </div>

      {/* Compte rendu global — indicateurs compacts, tout fermé par défaut : clic = dérouler, reclic = fermer */}
      <div className="mt-8 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Compte rendu global — cliquez sur un chiffre pour dérouler la liste correspondante</div>
      <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5" data-testid="amb-report-grid">
        {cards.map((c, i) => {
          const open = reportFilter === c.key;
          return (
            <motion.button key={c.key} type="button" onClick={() => setReportFilter((cur) => (cur === c.key ? "" : c.key))}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className={`group relative flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left shadow-sm transition-all duration-200 select-none hover:-translate-y-0.5 ${open ? "border-cyan bg-cyan/10 ring-1 ring-cyan/40 shadow-[0_0_15px_rgba(0,240,255,0.25)]" : "border-white/10 bg-slate-900/60 hover:border-cyan/40 hover:bg-slate-800/60"}`}
              data-testid={`amb-report-${c.key}`} aria-expanded={open}>
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg" style={{ background: `${c.c}1f`, color: c.c }}><c.icon size={14} /></span>
              <span className="min-w-0 flex-1">
                <span className="block font-display text-lg font-700 leading-tight tabular-nums text-white">{c.v}</span>
                <span className="block truncate text-[11px] font-medium uppercase tracking-wider text-ink-secondary">{c.l}</span>
              </span>
              <ChevronDown size={13} className={`flex-shrink-0 text-ink-tertiary transition-transform duration-200 ${open ? "rotate-180 text-cyan" : "group-hover:text-white"}`} />
            </motion.button>
          );
        })}
      </div>
      {!activeCard && (
        <p className="mt-3 rounded-xl border border-dashed border-white/15 px-4 py-4 text-center text-xs text-ink-tertiary" data-testid="amb-report-hint">
          Aucun indicateur ouvert. Cliquez par exemple sur « Invités » pour voir tous les invités, « Inscrits » pour les inscrits, etc. Recliquez pour refermer.
        </p>
      )}

      {/* Fiches des ambassadeurs correspondant à l'indicateur déroulé */}
      {activeCard && (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">
              <span>Fiches — {activeCard.l}</span>
              <span className="rounded-full bg-white/10 px-2 py-0.5 text-white">{fiches.length}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un ambassadeur…" data-testid="amb-search"
                  className="w-56 rounded-full border border-white/10 bg-surface py-2 pl-9 pr-4 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
              </div>
              <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="amb-country-filter"
                className="rounded-full border border-white/10 bg-surface px-4 py-2 text-sm text-white outline-none focus:border-cyan">
                <option value="">Tous les pays</option>
                {countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          {fiches.length === 0 ? (
            <p className="mt-4 rounded-xl border border-white/10 bg-surface p-6 text-center text-sm text-ink-tertiary" data-testid="amb-fiches-empty">Aucun ambassadeur pour cet indicateur.</p>
          ) : (
            <div className="mt-4 grid gap-3" data-testid="amb-fiches">
              {fiches.map((it) => (
                <div key={it.id} data-testid={`amb-fiche-${it.id}`}
                  className="rounded-xl border border-white/10 bg-surface p-4 transition-all hover:border-cyan/40">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-display text-base font-700 text-white">
                          {(it.first_name || it.last_name) ? `${it.first_name || ""} ${it.last_name || ""}`.trim() : (it.invite_name || "Sans nom")}
                        </span>
                        {it.permanent && <span className="rounded-full bg-electric/20 px-2 py-0.5 text-[9px] font-600 uppercase text-electric">Groupe</span>}
                        {it.committee && <span className="rounded-full bg-amber-400/20 px-2 py-0.5 text-[9px] font-600 uppercase text-amber-400">Comité</span>}
                        {isNew(it) && <span className="rounded-full bg-green-500/20 px-2 py-0.5 text-[9px] font-600 uppercase text-green-400">Nouveau</span>}
                        {it.status === "revoked"
                          ? <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[9px] font-600 uppercase text-red-400">Révoqué</span>
                          : <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-[9px] font-600 uppercase text-green-400">Actif</span>}
                        {it.email_invalid && <Tip label="Le serveur a refusé un e-mail envoyé à cette adresse (bounce) — elle est exclue des envois"><span className="inline-flex cursor-help items-center gap-1 rounded-full bg-red-500/20 px-2 py-0.5 text-[9px] font-600 uppercase text-red-400" data-testid={`amb-fiche-invalid-${it.id}`}><MailWarning size={10} /> E-mail refusé</span></Tip>}
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-secondary">
                        {(it.email || it.invite_email) ? (
                          <Tip label="Adresse e-mail — cliquez pour lui écrire directement">
                            <a href={`mailto:${it.email || it.invite_email}`} className="underline-offset-2 hover:text-cyan hover:underline" data-testid={`amb-fiche-mailto-${it.id}`}>✉ {it.email || it.invite_email}</a>
                          </Tip>
                        ) : <span>✉ En attente d'inscription</span>}
                        {it.country && <span>📍 {it.country}</span>}
                        <span className="font-mono text-cyan">{it.code}</span>
                        {it.used_at && <span className="text-ink-tertiary">Inscrit le {new Date(it.used_at).toLocaleDateString("fr-FR")}</span>}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      {it.invite_email && (
                        <button onClick={() => setMailPanel(mailPanel === it.id ? null : it.id)} data-testid={`amb-fiche-mail-${it.id}`} className="aca-btn-secondary inline-flex items-center gap-1.5 text-white"><Mail size={13} /> Mail{it.invite_sent_at && <CheckCircle2 size={12} className="text-green-400" />}</button>
                      )}
                      <button onClick={() => openJourney(it.id)} data-testid={`amb-fiche-analyse-${it.id}`} className="aca-btn-secondary inline-flex items-center gap-1.5 text-white"><Eye size={13} /> Analyse</button>
                      {!it.permanent && it.invite_email && (
                        <button onClick={() => toggleEmailInvalid(it)} data-testid={`amb-fiche-email-toggle-${it.id}`}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[11px] font-600 uppercase tracking-wide transition-colors ${it.email_invalid ? "border-red-400/50 bg-red-400/10 text-red-300 hover:border-red-400" : "border-white/15 text-white hover:border-cyan/50"}`}>
                          <MailWarning size={13} /> {it.email_invalid ? "E-mail valide" : "E-mail refusé"}
                        </button>
                      )}
                      <button onClick={() => setEditItem(it)} data-testid={`amb-fiche-edit-${it.id}`} className="aca-btn-primary inline-flex items-center gap-1.5"><Pencil size={13} /> Agrémenter</button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-4">
                    <Journey steps={it.steps} progress={it.progress} />
                    <div className="flex gap-4 text-[11px] text-ink-secondary">
                      <span title="Pages consultées"><EyeIcon size={11} className="mr-1 inline" />{it.page_views || 0}</span>
                      <span title="Idées"><Lightbulb size={11} className="mr-1 inline" />{it.ideas_count || 0}</span>
                      <span title="Retours"><MessageSquareHeart size={11} className="mr-1 inline" />{it.feedback_count || 0}</span>
                      {it.note && <span className="text-ink-tertiary">📝 {it.note}</span>}
                    </div>
                  </div>
                  {mailPanel === it.id && it.invite_email && (
                    <div className="mt-4 rounded-xl border border-cyan/30 bg-cyan/5 p-4" data-testid={`amb-mail-panel-${it.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-sm text-white">Envoyer l&apos;invitation à <span className="font-700 text-cyan">{it.invite_email}</span></div>
                        <div className="flex items-center gap-3">
                          {it.invite_sent_at && (
                            <span className="inline-flex items-center gap-1 text-xs text-green-400" data-testid={`amb-sent-badge-${it.id}`}>
                              <CheckCircle2 size={13} /> Envoyé le {new Date(it.invite_sent_at).toLocaleDateString("fr-FR")}
                            </span>
                          )}
                          <button onClick={() => sendInvite(it)} data-testid={`amb-send-confirm-${it.id}`} className="aca-btn-primary inline-flex items-center gap-1.5">
                            <Mail size={13} /> {it.invite_sent_at ? "Renvoyer" : "Envoyer"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Retours des e-mails groupés — analyse e-mail par e-mail (toujours visible) */}
      <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-retours"><MailOpen size={13} /> E-mails envoyés — analyse par e-mail</div>
      {(
        <div className="mt-4" data-testid="amb-mails-panel">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button onClick={() => setMailsView("mails")} data-testid="amb-mails-view-mails"
              className={`aca-btn-ghost border ${mailsView === "mails" ? "border-cyan/50 bg-cyan/10 text-cyan" : "border-white/10"}`}>
              Par e-mail
            </button>
            <button onClick={() => setMailsView("ambassadeurs")} data-testid="amb-mails-view-amb"
              className={`aca-btn-ghost border ${mailsView === "ambassadeurs" ? "border-cyan/50 bg-cyan/10 text-cyan" : "border-white/10"}`}>
              Par ambassadeur
            </button>
          </div>

          {mailsView === "ambassadeurs" ? (
          <div className="overflow-x-auto rounded-xl border border-white/10" data-testid="amb-mails-summary">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="border-b border-white/10 bg-surface text-[11px] uppercase tracking-wider text-ink-tertiary">
                <tr><th className="p-3">Ambassadeur</th><th className="p-3">Reçus</th><th className="p-3">Ouverts</th><th className="p-3">Lus</th><th className="p-3">Dernière ouverture</th><th className="p-3">Appareil</th><th className="p-3">État</th></tr>
              </thead>
              <tbody>
                {(mailSummary || []).map((s) => (
                  <tr key={s.email} className="border-b border-white/5 hover:bg-surface" data-testid={`amb-summary-${s.email}`}>
                    <td className="p-3"><div className="text-white">{s.name || "—"}</div><a href={`mailto:${s.email}`} className="text-xs text-ink-tertiary underline-offset-2 hover:text-cyan hover:underline" data-testid={`amb-summary-mailto-${s.email}`}>{s.email}</a></td>
                    <td className="p-3 tabular-nums text-white">{s.total}{s.failed ? <span className="ml-1 text-xs text-red-400" title="Envois en échec">({s.failed} échec{s.failed > 1 ? "s" : ""})</span> : null}</td>
                    <td className="p-3"><span className="tabular-nums text-white">{s.opened}/{s.total}</span> <span className={`ml-1 text-xs ${s.open_rate >= 50 ? "text-green-400" : "text-ink-tertiary"}`}>{s.open_rate}%</span></td>
                    <td className="p-3"><span className="tabular-nums text-white">{s.read}/{s.total}</span> <span className={`ml-1 text-xs ${s.read_rate >= 50 ? "text-green-400" : "text-ink-tertiary"}`}>{s.read_rate}%</span></td>
                    <td className="p-3 text-ink-secondary">{s.last_open_at ? new Date(s.last_open_at).toLocaleString("fr-FR") : "—"}</td>
                    <td className="max-w-[180px] truncate p-3 text-xs text-ink-tertiary" title={s.last_open_ua || ""}>{s.last_open_ua ? (s.last_open_ua.includes("Mobile") ? "Mobile" : "Ordinateur") : "—"}</td>
                    <td className="p-3">
                      {s.unsubscribed ? <span className="rounded-full bg-violet-500/15 px-2.5 py-1 text-[11px] font-600 uppercase text-violet-300">Désinscrit</span>
                        : s.failed > 0 && s.sent === 0 ? <span className="rounded-full bg-red-500/15 px-2.5 py-1 text-[11px] font-600 uppercase text-red-400">E-mail refusé</span>
                        : s.read > 0 ? <span className="rounded-full bg-green-500/15 px-2.5 py-1 text-[11px] font-600 uppercase text-green-400">Lecteur actif</span>
                        : s.opened > 0 ? <span className="rounded-full bg-cyan/15 px-2.5 py-1 text-[11px] font-600 uppercase text-cyan">Ouvre</span>
                        : <span className="rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-600 uppercase text-ink-secondary">Sans retour</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {mailSummary && mailSummary.length === 0 && <div className="p-10 text-center text-sm text-ink-tertiary">Aucun e-mail groupé envoyé pour le moment.</div>}
            {!mailSummary && <div className="p-10 text-center text-sm text-ink-tertiary">Chargement…</div>}
          </div>
          ) : (
          <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-white/10 bg-surface text-[11px] uppercase tracking-wider text-ink-tertiary">
              <tr><th className="p-3">Destinataire</th><th className="p-3">Objet</th><th className="p-3">Envoyé le</th><th className="p-3">Statut</th><th className="p-3">Ouvert</th><th className="p-3">Lu</th></tr>
            </thead>
            <tbody>
              {(mails || []).map((m) => (
                <tr key={m.id} className="border-b border-white/5 hover:bg-surface" data-testid={`amb-mail-row-${m.id}`}>
                  <td className="p-3"><div className="text-white">{m.name || "—"}</div><div className="text-xs text-ink-tertiary">{m.to}</div></td>
                  <td className="max-w-[220px] truncate p-3 text-ink-secondary">{m.subject}</td>
                  <td className="p-3 text-ink-secondary">{m.sent_at ? new Date(m.sent_at).toLocaleString("fr-FR") : "—"}</td>
                  <td className="p-3">
                    {m.unsubscribed ? <span className="rounded-full bg-violet-500/15 px-2.5 py-1 text-[11px] font-600 uppercase text-violet-300" data-testid={`amb-mail-unsub-${m.id}`}>Désinscrit</span>
                      : m.status === "envoye" ? <span className="rounded-full bg-cyan/15 px-2.5 py-1 text-[11px] font-600 uppercase text-cyan">Envoyé</span>
                      : m.status === "non_configure" ? <span className="rounded-full bg-amber-400/15 px-2.5 py-1 text-[11px] font-600 uppercase text-amber-300">SMTP non configuré</span>
                      : <span className="block max-w-[240px] rounded-full bg-red-500/15 px-2.5 py-1 text-[11px] font-600 normal-case text-red-400" data-testid={`amb-mail-fail-${m.id}`}>
                          Échec{m.status.startsWith("echec:") ? ` — ${m.status.slice(7).slice(0, 90)}` : ""}
                        </span>}
                  </td>
                  <td className="p-3">{m.opened_at ? <span className="inline-flex items-center gap-1 text-xs text-green-400"><MailOpen size={12} /> {new Date(m.opened_at).toLocaleDateString("fr-FR")}{m.opens > 1 ? ` (×${m.opens})` : ""}</span> : <span className="text-xs text-ink-tertiary">—</span>}</td>
                  <td className="p-3">{m.read_at ? <span className="inline-flex items-center gap-1 text-xs text-green-400"><CheckCircle2 size={12} /> {new Date(m.read_at).toLocaleDateString("fr-FR")}</span> : <span className="text-xs text-ink-tertiary">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {mails && mails.length === 0 && <div className="p-10 text-center text-sm text-ink-tertiary" data-testid="amb-mails-empty">Aucun e-mail groupé envoyé pour le moment.</div>}
          {!mails && <div className="p-10 text-center text-sm text-ink-tertiary">Chargement…</div>}
          </div>
          )}
        </div>
      )}

      {/* Classement des ambassadeurs les plus actifs */}
      {leaderboard.length > 0 && (
        <>
          <div className="mt-10 text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Classement des ambassadeurs les plus actifs</div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="amb-leaderboard">
            {leaderboard.map((it, i) => (
              <div key={it.id} className="relative overflow-hidden rounded-xl border border-white/10 bg-surface p-4" data-testid={`amb-leader-${i}`}>
                <div className="flex items-center gap-2">
                  <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-900 ${i === 0 ? "bg-amber-400 text-void" : "bg-white/10 text-white"}`}>{i + 1}</span>
                  <span className="truncate text-sm font-600 text-white">{(it.first_name || it.last_name) ? `${it.first_name || ""} ${it.last_name || ""}`.trim() : (it.invite_name || it.code)}</span>
                </div>
                <div className="mt-3 flex gap-3 text-[11px] text-ink-secondary">
                  <span title="Pages consultées">{it.page_views || 0} p</span>
                  <span title="Idées déposées">{it.ideas_count || 0} i</span>
                  <span title="Retours">{it.feedback_count || 0} r</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Liste */}
      <div className="mt-8 overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="border-b border-white/10 bg-surface text-[11px] uppercase tracking-wider text-ink-tertiary">
            <tr>
              <th className="p-4">Ambassadeur</th><th className="p-4">Code</th><th className="p-4">Parcours</th>
              <th className="cursor-pointer p-4 hover:text-cyan" onClick={() => setSortBy(sortBy === "page_views" ? null : "page_views")} data-testid="amb-sort-pages">Suivi {sortBy === "page_views" ? "↓" : ""}</th>
              <th className="cursor-pointer p-4 hover:text-cyan" onClick={() => setSortBy(sortBy === "ideas_count" ? null : "ideas_count")} data-testid="amb-sort-ideas">Idées {sortBy === "ideas_count" ? "↓" : ""}</th>
              <th className="cursor-pointer p-4 hover:text-cyan" onClick={() => setSortBy(sortBy === "feedback_count" ? null : "feedback_count")} data-testid="amb-sort-feedback">Retours {sortBy === "feedback_count" ? "↓" : ""}</th>
              <th className="p-4">Statut</th><th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={8} className="p-10 text-center text-ink-tertiary" data-testid="amb-empty">Aucun ambassadeur pour l’instant. Générez un code d’invitation pour commencer.</td></tr>
            )}
            {sortedList.map((it) => (
              <tr key={it.id} onClick={() => openJourney(it.id)} title="Cliquer pour ouvrir la fiche et l'analyse"
                className="cursor-pointer border-b border-white/5 transition-colors hover:bg-surface" data-testid={`amb-row-${it.id}`}>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <span className="font-600 text-white">{it.permanent ? (it.invite_name || "Code de groupe") : ((it.first_name || it.last_name) ? `${it.first_name || ""} ${it.last_name || ""}`.trim() : (it.invite_name || "—"))}</span>
                    {it.permanent && <span className="rounded-full bg-electric/20 px-2 py-0.5 text-[10px] font-600 uppercase text-electric" data-testid={`amb-permanent-${it.id}`}>Permanent</span>}
                    {it.committee && <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-2 py-0.5 text-[10px] font-600 uppercase text-amber-400" data-testid={`amb-committee-${it.id}`}><Scale size={10} /> Comité</span>}
                    {isNew(it) && <span className="rounded-full bg-green-500/20 px-2 py-0.5 text-[10px] font-600 uppercase text-green-400" data-testid={`amb-new-${it.id}`}>Nouveau</span>}
                    {it.email_invalid && <Tip label="Le serveur a refusé un e-mail envoyé à cette adresse (bounce) — elle est exclue des envois"><span className="inline-flex cursor-help items-center gap-1 rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-600 uppercase text-red-400" data-testid={`amb-invalid-${it.id}`}><MailWarning size={10} /> E-mail refusé</span></Tip>}
                  </div>
                  <div className="text-xs text-ink-secondary">
                    {it.permanent ? `${it.member_count} membre(s)` : (it.email || it.invite_email
                      ? <Tip label="Adresse e-mail — cliquez pour lui écrire directement"><a href={`mailto:${it.email || it.invite_email}`} onClick={(e) => e.stopPropagation()} className="text-ink-secondary underline-offset-2 hover:text-cyan hover:underline" data-testid={`amb-mailto-${it.id}`}>{it.email || it.invite_email}</a></Tip>
                      : "En attente d'inscription")}
                  </div>
                </td>
                <td className="p-4">
                  <Tip label="Code confidentiel — cliquez pour le copier">
                    <button onClick={(e) => { e.stopPropagation(); copy(it.code, "Code copié."); }} className="font-mono text-xs text-cyan hover:underline" data-testid={`amb-code-${it.id}`}>{it.code}</button>
                  </Tip>
                </td>
                <td className="p-4"><Journey steps={it.steps} progress={it.progress} /></td>
                <td className="p-4">
                  <div className="flex flex-col gap-1 text-[11px]">
                    {it.invite_sent_at
                      ? <Tip label="L'invitation a été envoyée par e-mail à cette personne"><span className="inline-flex items-center gap-1 text-cyan"><MailCheck size={12} /> Invité</span></Tip>
                      : <Tip label="Aucune invitation e-mail envoyée pour le moment"><span className="text-ink-tertiary">Non invité</span></Tip>}
                    {it.invite_open_count > 0 && <Tip label={`Le lien d'invitation a été ouvert ${it.invite_open_count} fois`}><span className="inline-flex items-center gap-1 text-amber-400"><MousePointerClick size={12} /> Ouvert ({it.invite_open_count})</span></Tip>}
                    {it.page_views > 0 && <Tip label="Nombre de pages consultées dans l'espace ambassadeur"><span className="inline-flex items-center gap-1 text-electric"><EyeIcon size={12} /> {it.page_views} page(s)</span></Tip>}
                  </div>
                </td>
                <td className="p-4 tabular-nums text-white"><Tip label="Nombre d'idées déposées par cet ambassadeur">{it.ideas_count}</Tip></td>
                <td className="p-4 tabular-nums text-white"><Tip label="Nombre de retours donnés sur les idées">{it.feedback_count}</Tip></td>
                <td className="p-4">
                  {it.status === "active"
                    ? <Tip label="Actif : le code fonctionne, l'ambassadeur peut s'inscrire et se connecter"><span className="rounded-full bg-green-500/10 px-2.5 py-1 text-xs text-green-400">Actif</span></Tip>
                    : <Tip label="Révoqué : le code est désactivé, l'accès est bloqué"><span className="rounded-full bg-red-500/10 px-2.5 py-1 text-xs text-red-400">Révoqué</span></Tip>}
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    <IconBtn title="Partager (WhatsApp, SMS…)" testid={`amb-share-${it.id}`} onClick={() => setShareItem(it)}><Share2 size={16} /></IconBtn>
                    <IconBtn title="Envoyer l'invitation par e-mail" testid={`amb-invite-${it.id}`} onClick={() => sendInvite(it)}><Mail size={16} /></IconBtn>
                    {!it.permanent && it.invite_email && (
                      <IconBtn title={it.email_invalid ? "Marquer l'e-mail comme valide (rétablir les envois)" : "Marquer l'e-mail comme refusé (le serveur a renvoyé un bounce)"} testid={`amb-email-toggle-${it.id}`} onClick={() => toggleEmailInvalid(it)}><MailWarning size={16} className={it.email_invalid ? "text-red-400" : ""} /></IconBtn>
                    )}
                    <IconBtn title="Copier le lien d'invitation" testid={`amb-link-${it.id}`} onClick={() => copy(inviteLink(it.code), "Lien d'invitation copié.")}><Link2 size={16} /></IconBtn>
                    <IconBtn title="Voir le parcours détaillé" testid={`amb-journey-${it.id}`} onClick={() => openJourney(it.id)}><Eye size={16} /></IconBtn>
                    <IconBtn title={it.committee ? "Retirer du comité de décision" : "Intégrer au comité de décision"} testid={`amb-committee-toggle-${it.id}`} onClick={() => toggleCommittee(it)}><Scale size={16} className={it.committee ? "text-amber-400" : ""} /></IconBtn>
                    <IconBtn title="Modifier la fiche" testid={`amb-edit-${it.id}`} onClick={() => setEditItem(it)}><Pencil size={16} /></IconBtn>
                    <IconBtn title={it.status === "active" ? "Révoquer" : "Réactiver"} testid={`amb-toggle-${it.id}`} onClick={() => toggleStatus(it)}>{it.status === "active" ? <Ban size={16} /> : <RotateCcw size={16} />}</IconBtn>
                    <IconBtn title="Supprimer" testid={`amb-delete-${it.id}`} onClick={() => remove(it.id)}><Trash2 size={16} /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {createOpen && <CreateModal onClose={() => setCreateOpen(false)} onDone={(link) => { setCreateOpen(false); load(); if (link) copy(link, "Code créé — lien copié !"); }} inviteLink={inviteLink} />}
      {manualOpen && <ManualModal onClose={() => setManualOpen(false)} onDone={() => { setManualOpen(false); load(); }} />}
      {importOpen && <ImportModal onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />}
      {mailOpen && <MailModal onClose={() => setMailOpen(false)} baseUrl={baseUrl} />}
      {editItem && <EditModal item={editItem} onClose={() => setEditItem(null)} onDone={() => { setEditItem(null); load(); }} />}
      {journey && <JourneyModal data={journey} onClose={() => setJourney(null)} onEdit={() => { setEditItem(journey.ambassador); setJourney(null); }} inviteLink={inviteLink} copy={copy} />}
      {shareItem && <ShareModal it={shareItem} link={inviteLink(shareItem.code)} message={shareMessage(shareItem)} onClose={() => setShareItem(null)} onEmail={() => { setShareItem(null); sendInvite(shareItem); }} copy={copy} />}
      {bulkInvite && <BulkInviteModal state={bulkInvite} setState={setBulkInvite} onConfirm={doBulkInvite} />}
      {mailAll && <MailAllModal state={mailAll} setState={setMailAll} onSent={() => { setMailAll(null); setMails(null); loadMailJob(); }} />}
    </div>
  );

  function openJourney(id) {
    api.get(`/admin/ambassadors/${id}/journey`).then((r) => setJourney(r.data)).catch((e) => toast.error(apiErr(e)));
  }
}

function Tip({ label, children }) {
  // Infobulle française instantanée au survol (pour tout élément : icône, badge, chiffre...).
  return (
    <span className="group relative inline-flex">
      {children}
      <span role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg border border-cyan/30 bg-elevated px-3 py-1.5 text-[11px] font-500 text-white opacity-0 shadow-[0_4px_16px_rgba(0,0,0,0.5)] transition-all duration-150 group-hover:translate-y-0.5 group-hover:opacity-100">
        {label}
      </span>
    </span>
  );
}

function Journey({ steps, progress }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-1">
        {STEP_DEFS.map((s) => (
          <Tip key={s.key} label={`${s.label} — ${steps?.[s.key] ? "fait" : "pas encore"}`}>
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full border ${steps?.[s.key] ? "border-cyan bg-cyan/15 text-cyan" : "border-white/10 text-ink-tertiary"}`}>
              <s.icon size={12} />
            </span>
          </Tip>
        ))}
      </div>
      <Tip label={`Étapes du parcours accomplies : ${progress} sur 5`}>
        <span className="text-xs tabular-nums text-ink-secondary">{progress}/5</span>
      </Tip>
    </div>
  );
}

function IconBtn({ children, title, onClick, testid }) {
  // Infobulle française instantanée au survol : explique à quoi correspond l'icône.
  return (
    <span className="group relative inline-flex">
      <button onClick={onClick} aria-label={title} data-testid={testid}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-secondary transition-colors hover:bg-white/5 hover:text-cyan">
        {children}
      </button>
      {title && (
        <span role="tooltip"
          className="pointer-events-none absolute right-full top-1/2 z-50 mr-2 -translate-y-1/2 whitespace-nowrap rounded-lg border border-cyan/30 bg-elevated px-3 py-1.5 text-[11px] font-500 text-white opacity-0 shadow-[0_4px_16px_rgba(0,0,0,0.5)] transition-all duration-150 group-hover:-translate-x-1 group-hover:opacity-100">
          {title}
        </span>
      )}
    </span>
  );
}

function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/70 p-4 aca-modal" data-testid={testid}>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-elevated p-6 sm:p-8">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-display text-xl font-700 text-white">{title}</h3>
          <button onClick={onClose} data-testid="amb-modal-close" className="text-ink-secondary hover:text-white"><X size={20} /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid, required }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{label}</span>
      <input type={type} value={value} onChange={onChange} data-testid={testid} required={required}
        className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
    </label>
  );
}

function CreateModal({ onClose, onDone, inviteLink }) {
  const [f, setF] = useState({ invite_name: "", invite_email: "", note: "", permanent: false });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { data } = await api.post("/admin/ambassadors", f);
      onDone(inviteLink(data.code));
    } catch (err) { toast.error(apiErr(err)); setSaving(false); }
  };
  return (
    <Modal title="Générer un code d'invitation" onClose={onClose} testid="amb-create-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="amb-create-form">
        <p className="text-sm text-ink-secondary">Un code unique et confidentiel sera généré. Partagez le lien uniquement avec la personne invitée.</p>
        <Input label="Nom de l'invité (optionnel)" value={f.invite_name} onChange={(e) => setF({ ...f, invite_name: e.target.value })} testid="amb-create-name" />
        <Input label="E-mail de l'invité (optionnel)" type="email" value={f.invite_email} onChange={(e) => setF({ ...f, invite_email: e.target.value })} testid="amb-create-email" />
        <Input label="Note interne (optionnel)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} testid="amb-create-note" />
        <button type="button" onClick={() => setF({ ...f, permanent: !f.permanent })} data-testid="amb-create-permanent"
          className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-colors ${f.permanent ? "border-cyan bg-cyan/10" : "border-white/10 bg-surface"}`}>
          <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${f.permanent ? "border-cyan bg-cyan text-void" : "border-white/20"}`}>{f.permanent && <CheckCircle2 size={14} />}</span>
          <span>
            <span className="block text-sm font-600 text-white">Code permanent (de groupe)</span>
            <span className="mt-0.5 block text-xs text-ink-secondary">Utilisable par plusieurs personnes, jamais épuisé ni révoqué automatiquement. Idéal pour un groupe. Chaque membre est suivi individuellement.</span>
          </span>
        </button>
        <button type="submit" disabled={saving} data-testid="amb-create-submit" className="aca-btn-primary inline-flex w-full items-center justify-center gap-2 disabled:opacity-60">
          {saving ? "Génération…" : "Générer et copier le lien"} <KeyRound size={15} />
        </button>
      </form>
    </Modal>
  );
}

function ManualModal({ onClose, onDone }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", country: "France", password: "", note: "", committee: false });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 6) return toast.error("Mot de passe : 6 caractères minimum.");
    setSaving(true);
    try { await api.post("/admin/ambassadors/manual", f); toast.success("Ambassadeur ajouté."); onDone(); }
    catch (err) { toast.error(apiErr(err)); setSaving(false); }
  };
  return (
    <Modal title="Ajouter un ambassadeur manuellement" onClose={onClose} testid="amb-manual-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="amb-manual-form">
        <p className="text-sm text-ink-secondary">Crée directement un compte ambassadeur. Communiquez-lui l’e-mail et le mot de passe.</p>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Prénom" value={f.first_name} onChange={set("first_name")} testid="amb-manual-firstname" required />
          <Input label="Nom" value={f.last_name} onChange={set("last_name")} testid="amb-manual-lastname" required />
        </div>
        <Input label="E-mail" type="email" value={f.email} onChange={set("email")} testid="amb-manual-email" required />
        <Input label="Mot de passe" type="text" value={f.password} onChange={set("password")} testid="amb-manual-password" required />
        <Input label="Note interne (optionnel)" value={f.note} onChange={set("note")} testid="amb-manual-note" />
        <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 bg-surface p-4" data-testid="amb-manual-committee-row">
          <input type="checkbox" checked={f.committee} onChange={(e) => setF({ ...f, committee: e.target.checked })} data-testid="amb-manual-committee" className="h-4 w-4 accent-amber-400" />
          <span className="text-sm text-white">Ajouter directement au comité de décision</span>
        </label>
        <button type="submit" disabled={saving} data-testid="amb-manual-submit" className="aca-btn-primary inline-flex w-full items-center justify-center gap-2 disabled:opacity-60">
          {saving ? "Création…" : "Créer l'ambassadeur"} <UserPlus size={15} />
        </button>
      </form>
    </Modal>
  );
}

const CSV_HEADER_MAP = {
  "prenom": "first_name", "prénom": "first_name", "first_name": "first_name", "firstname": "first_name",
  "nom": "last_name", "last_name": "last_name", "lastname": "last_name",
  "nom complet": "__name", "name": "__name", "nom prenom": "__name", "nom prénom": "__name",
  "email": "email", "e-mail": "email", "mail": "email", "adresse mail": "email", "adresse e-mail": "email", "courriel": "email",
  "portable": "phone_mobile", "mobile": "phone_mobile", "tel portable": "phone_mobile", "téléphone portable": "phone_mobile",
  "telephone portable": "phone_mobile", "phone_mobile": "phone_mobile", "gsm": "phone_mobile", "tel": "phone_mobile", "téléphone": "phone_mobile", "telephone": "phone_mobile",
  "fixe": "phone_landline", "tel fixe": "phone_landline", "téléphone fixe": "phone_landline", "telephone fixe": "phone_landline", "phone_landline": "phone_landline", "landline": "phone_landline",
  "organisation": "organization", "média": "organization", "media": "organization", "organization": "organization", "société": "organization", "societe": "organization", "entreprise": "organization",
  "fonction": "role_title", "poste": "role_title", "role": "role_title", "rôle": "role_title", "role_title": "role_title", "titre": "role_title",
  "adresse": "address", "adresse personnelle": "address", "address": "address",
  "cp": "postal_code", "code postal": "postal_code", "postal_code": "postal_code", "zip": "postal_code",
  "ville": "city", "city": "city",
  "pays": "country", "country": "country",
  "site": "website", "site web": "website", "website": "website", "url": "website", "lien": "website",
  "note": "note", "notes": "note", "remarque": "note",
};

function parseCsvContacts(text) {
  const lines = (text || "").split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length < 2) return { rows: [], invalid: 0, headers: [] };
  const delim = (lines[0].match(/;/g) || []).length > (lines[0].match(/,/g) || []).length ? ";" : ",";
  const splitLine = (line) => {
    const out = []; let cur = ""; let q = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
      else if (ch === delim && !q) { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur);
    return out.map((c) => c.trim());
  };
  const rawHeaders = splitLine(lines[0]);
  const keys = rawHeaders.map((h) => CSV_HEADER_MAP[h.toLowerCase().trim()] || null);
  const rows = []; let invalid = 0;
  for (let i = 1; i < lines.length; i++) {
    const cells = splitLine(lines[i]);
    const obj = {};
    cells.forEach((val, idx) => { const k = keys[idx]; if (k && val) obj[k] = val; });
    if (obj.__name && !obj.first_name && !obj.last_name) {
      const parts = obj.__name.split(/\s+/);
      obj.first_name = parts[0]; obj.last_name = parts.slice(1).join(" ");
    }
    delete obj.__name;
    if (!obj.email) { invalid++; continue; }
    rows.push(obj);
  }
  return { rows, invalid, headers: rawHeaders };
}

function ImportModal({ onClose, onDone }) {
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [importing, setImporting] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setText(String(reader.result || "")); setParsed(null); };
    reader.readAsText(file, "utf-8");
  };

  const analyze = () => {
    const res = parseCsvContacts(text);
    setParsed(res);
    if (res.rows.length === 0) toast.error("Aucune ligne valide trouvée (colonne e-mail requise).");
  };

  const doImport = async () => {
    if (!parsed || parsed.rows.length === 0) return;
    setImporting(true);
    try {
      const { data } = await api.post("/admin/ambassadors/import-contacts", { contacts: parsed.rows });
      toast.success(`Import terminé : ${data.created} créé(s), ${data.updated} mis à jour, ${data.skipped} ignoré(s).`, { duration: 8000 });
      onDone();
    } catch (e) { toast.error(apiErr(e)); setImporting(false); }
  };

  return (
    <Modal title="Importer des fiches (CSV)" onClose={onClose} testid="amb-import-modal">
      <div className="space-y-4">
        <div className="rounded-xl border border-white/10 bg-surface p-4 text-xs leading-relaxed text-ink-secondary">
          <div className="mb-2 flex items-center gap-2 text-white"><FileText size={15} className="text-cyan" /> Format attendu</div>
          La première ligne = en-têtes. Colonnes reconnues (séparateur <span className="text-white">,</span> ou <span className="text-white">;</span>) :
          <span className="text-white"> Prénom, Nom, Email, Portable, Fixe, Organisation, Fonction, Adresse, Code postal, Ville, Pays, Site, Note</span>.
          La colonne <span className="text-white">Email</span> est obligatoire. Les e-mails déjà présents sont mis à jour.
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="aca-btn-secondary inline-flex cursor-pointer items-center gap-2 text-white" data-testid="amb-import-file-label">
            <Upload size={14} /> Choisir un fichier .csv
            <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="amb-import-file" />
          </label>
          <span className="text-xs text-ink-tertiary">ou collez le contenu ci-dessous</span>
        </div>

        <textarea rows={7} value={text} onChange={(e) => { setText(e.target.value); setParsed(null); }} data-testid="amb-import-textarea"
          placeholder={"Prénom,Nom,Email,Portable,Organisation,Pays\nMarie,Dupont,marie@presse.fr,0612345678,Le Monde,France"}
          className="w-full resize-none rounded-xl border border-white/10 bg-surface px-4 py-3 font-mono text-xs text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />

        {parsed && (
          <div className="rounded-xl border border-cyan/30 bg-cyan/5 p-4 text-sm text-white" data-testid="amb-import-preview">
            <span className="font-700 text-cyan">{parsed.rows.length}</span> fiche(s) valide(s) prête(s) à importer
            {parsed.invalid > 0 && <span className="text-ink-secondary"> · {parsed.invalid} ligne(s) sans e-mail ignorée(s)</span>}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="aca-btn-secondary text-white">Annuler</button>
          {!parsed ? (
            <button onClick={analyze} disabled={!text.trim()} data-testid="amb-import-analyze" className="aca-btn-primary disabled:opacity-50">Analyser</button>
          ) : (
            <button onClick={doImport} disabled={importing || parsed.rows.length === 0} data-testid="amb-import-confirm" className="aca-btn-primary disabled:opacity-50">
              {importing ? "Import…" : `Importer ${parsed.rows.length} fiche(s)`}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}


function MailModal({ onClose, baseUrl }) {
  const KEYS = [
    { key: "ambassador_invite", tab: "Invitation", relance: false },
    { key: "ambassador_relance_1", tab: "Relance 1", relance: true },
    { key: "ambassador_relance_2", tab: "Relance 2", relance: true },
    { key: "ambassador_relance_3", tab: "Relance 3", relance: true },
    { key: "ambassador_relance_4", tab: "Relance 4", relance: true },
  ];
  const [tpls, setTpls] = useState({});
  const [active, setActive] = useState("ambassador_invite");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/email-templates").then((r) => {
      const map = {};
      r.data.forEach((t) => { map[t.key] = t; });
      setTpls(map);
    }).catch((e) => toast.error(apiErr(e)));
  }, []);

  useEffect(() => {
    const t = tpls[active];
    if (t) { setSubject(t.subject || ""); setBody(t.body || ""); }
  }, [active, tpls]);

  const meta = KEYS.find((k) => k.key === active);
  const preview = (body || "")
    .replaceAll("{nom}", "Marie Dupont")
    .replaceAll("{code}", "AC7K2M")
    .replaceAll("{lien}", `${baseUrl}/ambassadeur/AC7K2M`);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/email-templates/${active}`, { subject, body });
      setTpls((s) => ({ ...s, [active]: data }));
      toast.success("Modèle enregistré.");
    } catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  const sendRelance = async () => {
    if (!window.confirm(`Envoyer « ${meta.tab} » aux invités qui n'ont pas encore ouvert leur invitation ?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/ambassadors/remind", { base_url: baseUrl, template: active });
      if (data.total === 0) toast.info("Aucun invité à relancer (tous ont ouvert leur lien ou aucun n'a encore été invité).");
      else if (!data.smtp_configured) toast.warning(`SMTP non configuré : ${data.total} relance(s) journalisée(s).`, { duration: 8000 });
      else toast.success(`${data.sent}/${data.total} relance(s) envoyée(s).`);
    } catch (e) { toast.error(apiErr(e)); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/70 p-4 aca-modal" data-testid="amb-mail-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-white/10 bg-elevated p-6 sm:p-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl font-700 text-white">Mails ambassadeurs</h3>
            <p className="mt-1 text-sm text-ink-secondary">Visualisez et modifiez le mail d&apos;invitation et les 4 modèles de relance à tout moment.</p>
          </div>
          <button onClick={onClose} data-testid="amb-mail-close" className="text-ink-secondary hover:text-white"><X size={20} /></button>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          {KEYS.map((k) => (
            <button key={k.key} onClick={() => setActive(k.key)} data-testid={`amb-mail-tab-${k.key}`}
              className={`rounded-full px-4 py-2 text-xs font-600 uppercase tracking-wide transition-all ${active === k.key ? "bg-cyan text-void" : "border border-white/15 text-white hover:border-cyan/50"}`}>
              {k.tab}
            </button>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Objet</span>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="amb-mail-subject"
                className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Corps du message</span>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={14} data-testid="amb-mail-body"
                className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 font-mono text-xs text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
            </label>
            <p className="text-[11px] text-ink-tertiary">Champs dynamiques : <span className="font-mono text-cyan">{"{nom}"}</span> <span className="font-mono text-cyan">{"{code}"}</span> <span className="font-mono text-cyan">{"{lien}"}</span></p>
            <div className="flex flex-wrap gap-3">
              <button onClick={save} disabled={saving} data-testid="amb-mail-save"
                className="aca-btn-primary inline-flex items-center gap-2 disabled:opacity-60">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
              {meta?.relance && (
                <button onClick={sendRelance} disabled={sending} data-testid="amb-mail-send-relance"
                  className="aca-btn-secondary inline-flex items-center gap-2 text-white disabled:opacity-60">
                  <Send size={15} /> {sending ? "Envoi…" : "Envoyer aux non-ouverts"}
                </button>
              )}
            </div>
          </div>

          <div>
            <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Aperçu</span>
            <div className="overflow-hidden rounded-xl border border-white/10" data-testid="amb-mail-preview">
              <div className="bg-[#0a0e1a] py-5 text-center">
                <img src={`${baseUrl}/email-logo.png`} alt="APP CHALLENGE ACADEMY" className="mx-auto h-10" />
              </div>
              <div className="bg-white p-5">
                <div className="mb-3 border-b border-black/10 pb-2 text-sm font-700 text-[#111827]">{subject}</div>
                <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-[#111827]">{preview}</div>
              </div>
              <div className="bg-[#f4f6fb] py-3 text-center text-[11px] text-[#8a94a8]">appchallengeacademy.com</div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function EditModal({ item, onClose, onDone }) {
  const nameParts = (item.invite_name || "").trim().split(/\s+/).filter(Boolean);
  const [f, setF] = useState({
    first_name: item.first_name || nameParts[0] || "",
    last_name: item.last_name || nameParts.slice(1).join(" ") || "",
    invite_email: item.invite_email || "",
    phone_mobile: item.phone_mobile || "",
    phone_landline: item.phone_landline || "",
    organization: item.organization || "",
    role_title: item.role_title || "",
    address: item.address || "",
    postal_code: item.postal_code || "",
    city: item.city || "",
    country: item.country || "",
    website: item.website || "",
    note: item.note || "",
    status: item.status || "active",
  });
  const [committee, setCommittee] = useState(!!item.committee);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.put(`/admin/ambassadors/${item.id}`, f);
      if (committee !== !!item.committee) {
        if (!await adminPutProtected(`/admin/ambassadors/${item.id}/committee`, { committee })) { setSaving(false); return; }
      }
      toast.success("Fiche mise à jour.");
      onDone();
    } catch (err) { toast.error(apiErr(err)); setSaving(false); }
  };
  return (
    <Modal title="Modifier la fiche" onClose={onClose} testid="amb-edit-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="amb-edit-form">
        <div className="text-[11px] uppercase tracking-[0.2em] text-cyan">Identité de l’interlocuteur</div>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Prénom" value={f.first_name} onChange={set("first_name")} testid="amb-edit-first-name" />
          <Input label="Nom" value={f.last_name} onChange={set("last_name")} testid="amb-edit-last-name" />
        </div>
        <Input label="Adresse e-mail" type="email" value={f.invite_email} onChange={set("invite_email")} testid="amb-edit-email" />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Téléphone portable" value={f.phone_mobile} onChange={set("phone_mobile")} testid="amb-edit-phone-mobile" />
          <Input label="Téléphone fixe" value={f.phone_landline} onChange={set("phone_landline")} testid="amb-edit-phone-landline" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Organisation / Média" value={f.organization} onChange={set("organization")} testid="amb-edit-organization" />
          <Input label="Fonction / Poste" value={f.role_title} onChange={set("role_title")} testid="amb-edit-role-title" />
        </div>

        <div className="pt-2 text-[11px] uppercase tracking-[0.2em] text-cyan">Coordonnées</div>
        <Input label="Adresse personnelle" value={f.address} onChange={set("address")} testid="amb-edit-address" />
        <div className="grid grid-cols-3 gap-4">
          <Input label="Code postal" value={f.postal_code} onChange={set("postal_code")} testid="amb-edit-postal-code" />
          <Input label="Ville" value={f.city} onChange={set("city")} testid="amb-edit-city" />
          <Input label="Pays" value={f.country} onChange={set("country")} testid="amb-edit-country" />
        </div>
        <Input label="Site web / Lien" value={f.website} onChange={set("website")} testid="amb-edit-website" />

        <label className="block">
          <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Note interne</span>
          <textarea rows={3} value={f.note} onChange={set("note")} data-testid="amb-edit-note"
            className="w-full resize-none rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Statut</span>
          <select value={f.status} onChange={set("status")} data-testid="amb-edit-status"
            className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan">
            <option value="active">Actif</option>
            <option value="revoked">Révoqué</option>
          </select>
        </label>
        <button type="button" onClick={() => setCommittee(!committee)} data-testid="amb-edit-committee"
          className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-colors ${committee ? "border-amber-400 bg-amber-400/10" : "border-white/10 bg-surface"}`}>
          <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${committee ? "border-amber-400 bg-amber-400 text-void" : "border-white/20"}`}>{committee && <CheckCircle2 size={14} />}</span>
          <span className="flex items-center gap-2 text-sm font-600 text-white"><Scale size={15} className="text-amber-400" /> Membre du comité de décision</span>
        </button>
        <button type="submit" disabled={saving} data-testid="amb-edit-submit" className="aca-btn-primary inline-flex w-full items-center justify-center gap-2 disabled:opacity-60">
          {saving ? "Enregistrement…" : "Enregistrer"} <CheckCircle2 size={15} />
        </button>
        <NavigationAnalysis userId={item.used_by_user_id} clicks={item.invite_open_count ?? 0} />
      </form>
    </Modal>
  );
}

function JourneyModal({ data, onClose, onEdit, inviteLink, copy }) {
  const a = data.ambassador;
  const name = (a.first_name || a.last_name) ? `${a.first_name || ""} ${a.last_name || ""}`.trim() : (a.invite_name || a.code);
  return (
    <Modal title={`Parcours — ${name}`} onClose={onClose} testid="amb-journey-modal">
      <div className="space-y-6">
        <div className="flex justify-end">
          <button onClick={onEdit} data-testid="amb-journey-edit" className="aca-btn-primary inline-flex items-center gap-1.5"><Pencil size={13} /> Agrémenter la fiche</button>
        </div>
        <div className="rounded-xl border border-white/10 bg-surface p-4">
          <div className="flex items-center justify-between">
            <button onClick={() => copy(inviteLink(a.code), "Lien copié.")} className="flex items-center gap-2 font-mono text-sm text-cyan hover:underline"><Copy size={14} /> {a.code}</button>
            <span className="text-xs text-ink-secondary">{a.email || a.invite_email
              ? <a href={`mailto:${a.email || a.invite_email}`} className="underline-offset-2 hover:text-cyan hover:underline">{a.email || a.invite_email}</a>
              : "—"}</span>
          </div>
          <div className="mt-4"><Journey steps={a.steps} progress={a.progress} /></div>
          <div className="mt-4 grid grid-cols-4 gap-3 text-center">
            <Mini label="Connexions" value={a.login_count} />
            <Mini label="Pages" value={a.page_views} />
            <Mini label="Idées" value={a.ideas_count} />
            <Mini label="Retours" value={a.feedback_count} />
          </div>
          {a.note && <p className="mt-4 rounded-lg bg-void p-3 text-sm text-ink-secondary"><span className="text-ink-tertiary">Note : </span>{a.note}</p>}
        </div>

        {a.permanent && (data.members || []).length > 0 && (
          <div data-testid="amb-journey-members">
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Membres du code de groupe ({data.members.length})</div>
            <div className="mt-3 space-y-2">
              {data.members.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-void p-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm text-white">{m.name || "—"}</div>
                    <div className="truncate text-xs text-ink-tertiary">{m.email}</div>
                  </div>
                  <div className="flex shrink-0 gap-3 text-[11px] text-ink-secondary">
                    <span title="Pages">{m.pages}p</span>
                    <span title="Idées">{m.ideas}i</span>
                    <span title="Retours">{m.feedbacks}r</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Chronologie du parcours</div>
          <div className="mt-3 space-y-3">
            {data.events.length === 0 && <p className="text-sm text-ink-tertiary">Aucune activité enregistrée pour l’instant.</p>}
            {data.events.map((ev) => (
              <div key={ev.id} className="flex gap-3">
                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-cyan" />
                <div>
                  <div className="text-sm text-white">{ev.label}</div>
                  <div className="text-[11px] text-ink-tertiary">{ev.at ? new Date(ev.at).toLocaleString("fr-FR") : ""}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {data.ideas.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Idées déposées</div>
            <div className="mt-3 space-y-2">
              {data.ideas.map((p) => (
                <div key={p.id} className="rounded-lg border border-white/10 bg-void p-3 text-sm">
                  <span className="text-white">{p.title || "Sans titre"}</span>
                  <span className="ml-2 text-xs text-ink-tertiary">{p.ref} · {p.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.feedbacks.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-tertiary">Retours envoyés</div>
            <div className="mt-3 space-y-2">
              {data.feedbacks.map((fb) => (
                <div key={fb.id} className="rounded-lg border border-white/10 bg-void p-3 text-sm text-ink-secondary">
                  {fb.rating ? <span className="text-yellow-400">{"★".repeat(fb.rating)}</span> : null}
                  {fb.liked && <p className="mt-1"><span className="text-ink-tertiary">Aimé : </span>{fb.liked}</p>}
                  {fb.improve && <p className="mt-1"><span className="text-ink-tertiary">À améliorer : </span>{fb.improve}</p>}
                  {fb.message && <p className="mt-1">{fb.message}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function ShareModal({ it, link, message, onClose, onEmail, copy }) {
  const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const sms = `sms:?&body=${encodeURIComponent(message)}`;
  const mailto = `mailto:${it.invite_email || ""}?subject=${encodeURIComponent("Invitation privée — Comité des Ambassadeurs")}&body=${encodeURIComponent(message)}`;
  return (
    <Modal title={`Partager l'invitation — ${it.invite_name || it.code}`} onClose={onClose} testid="amb-share-modal">
      <div className="space-y-4">
        <div className="rounded-xl border border-white/10 bg-surface p-4">
          <div className="text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Code personnel</div>
          <div className="mt-1 font-mono text-lg font-700 tracking-widest text-cyan">{it.code}</div>
          <div className="mt-2 break-all text-xs text-ink-secondary">{link}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-void p-4">
          <div className="text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Message d’invitation</div>
          <p className="mt-2 whitespace-pre-line text-sm text-ink-secondary">{message}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <a href={wa} target="_blank" rel="noreferrer" data-testid="amb-share-whatsapp" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-sm font-600 text-black transition-transform hover:-translate-y-0.5"><MessageCircle size={16} /> WhatsApp</a>
          <a href={sms} data-testid="amb-share-sms" className="inline-flex items-center justify-center gap-2 rounded-full bg-electric px-4 py-3 text-sm font-600 text-void transition-transform hover:-translate-y-0.5"><Smartphone size={16} /> SMS</a>
          <button onClick={() => copy(message, "Message copié.")} data-testid="amb-share-copy-msg" className="aca-btn-secondary inline-flex items-center justify-center gap-2 text-white"><Copy size={16} /> Copier le message</button>
          <button onClick={onEmail} data-testid="amb-share-email" className="aca-btn-primary inline-flex items-center justify-center gap-2"><Mail size={16} /> E-mail</button>
        </div>
        <a href={mailto} className="block text-center text-xs text-ink-tertiary hover:text-cyan" data-testid="amb-share-mailto">Ou ouvrir dans votre messagerie</a>
      </div>
    </Modal>
  );
}

function BulkInviteModal({ state, setState, onConfirm }) {
  const allEligible = state.eligible || [];
  const [scope, setScope] = useState("all");
  const countries = [...new Set(allEligible.map((it) => it.country).filter(Boolean))].sort();
  const filtered = scope === "all" ? allEligible : allEligible.filter((it) => it.country === scope);
  const scopeLabel = scope === "all" ? "tous pays confondus" : `du pays « ${scope} »`;
  return (
    <Modal title="Envoyer les invitations par e-mail" onClose={() => setState(null)} testid="amb-bulk-modal">
      {state.step === 1 ? (
        <div className="space-y-5" data-testid="amb-bulk-step1">
          <div>
            <div className="mb-2 text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Portée de l’envoi</div>
            <div className="flex flex-wrap gap-2" data-testid="amb-bulk-scope">
              <button onClick={() => setScope("all")} data-testid="amb-bulk-scope-all"
                className={`rounded-full px-4 py-2 text-xs font-600 transition-all ${scope === "all" ? "bg-cyan text-void" : "border border-white/15 text-white hover:border-cyan/50"}`}>
                Tous les pays ({allEligible.length})
              </button>
              {countries.map((c) => {
                const n = allEligible.filter((it) => it.country === c).length;
                return (
                  <button key={c} onClick={() => setScope(c)} data-testid={`amb-bulk-scope-${c}`}
                    className={`rounded-full px-4 py-2 text-xs font-600 transition-all ${scope === c ? "bg-cyan text-void" : "border border-white/15 text-white hover:border-cyan/50"}`}>
                    {c} ({n})
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-xl border border-amber-400/30 bg-amber-400/5 p-4">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-400" />
            <p className="text-sm leading-relaxed text-ink-secondary">
              Vous êtes sur le point d’envoyer l’e-mail d’invitation personnalisé (code + lien unique) à
              <span className="font-700 text-white"> {filtered.length} ambassadeur(s) actif(s)</span> {scopeLabel} disposant d’une adresse e-mail.
              Chaque destinataire recevra <span className="text-white">son propre code</span>.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setState(null)} className="aca-btn-secondary text-white">Annuler</button>
            <button onClick={() => setState({ ...state, step: 2, ids: filtered.map((it) => it.id), count: filtered.length, scopeLabel })} disabled={filtered.length === 0} data-testid="amb-bulk-step1-continue" className="aca-btn-primary disabled:opacity-50">Continuer</button>
          </div>
        </div>
      ) : (
        <div className="space-y-5" data-testid="amb-bulk-step2">
          <p className="text-sm leading-relaxed text-ink-secondary">
            Confirmez-vous l’envoi définitif des invitations à <span className="font-700 text-white">{state.count}</span> destinataire(s) {state.scopeLabel} ?
            Cette action enverra les e-mails immédiatement.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setState({ ...state, step: 1 })} className="aca-btn-secondary text-white">Retour</button>
            <button onClick={() => onConfirm(state.ids)} data-testid="amb-bulk-confirm" className="aca-btn-primary">Envoyer maintenant</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-lg bg-void p-3">
      <div className="font-display text-xl font-900 tabular-nums text-white">{value ?? 0}</div>
      <div className="text-[11px] text-ink-tertiary">{label}</div>
    </div>
  );
}

// Libellés et modèles programmés (éditables dans « Mails (voir / modifier) »).
const MAIL_ALL_KINDS = {
  bulk: { label: "E-mail à tous — Invitation", tpl: "ambassador_invite" },
  relance_1: { label: "Relance 1", tpl: "ambassador_relance_1" },
  relance_2: { label: "Relance 2", tpl: "ambassador_relance_2" },
  relance_3: { label: "Relance 3", tpl: "ambassador_relance_3" },
  relance_4: { label: "Relance 4", tpl: "ambassador_relance_4" },
};

// Modale d'envoi groupé : envoie le mail DÉJÀ PROGRAMMÉ dans « Mails »,
// personnalisé ambassadeur par ambassadeur ({nom} {code} {lien}).
// Double confirmation. Conforme réglementation (désinscription auto, exclusions).
function MailAllModal({ state, setState, onSent }) {
  const meta = MAIL_ALL_KINDS[state.kind] || MAIL_ALL_KINDS.bulk;
  const [tpl, setTpl] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/email-templates").then((r) => {
      const t = r.data.find((x) => x.key === meta.tpl);
      setTpl(t || { subject: "", body: "" });
    }).catch((e) => toast.error(apiErr(e)));
  }, [meta.tpl]);

  // Aperçu personnalisé (exemple) : chaque ambassadeur recevra SES nom, code et lien.
  const preview = (tpl?.body || "")
    .replaceAll("{nom}", "Marie Dupont")
    .replaceAll("{code}", "AC7K2M")
    .replaceAll("{lien}", "https://appchallengeacademy.com/ambassadeur/AC7K2M");
  const previewSubject = (tpl?.subject || "")
    .replaceAll("{nom}", "Marie Dupont")
    .replaceAll("{code}", "AC7K2M")
    .replaceAll("{lien}", "https://appchallengeacademy.com/ambassadeur/AC7K2M");

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/ambassadors/mail-all", { kind: state.kind });
      toast.success(`${meta.label} : expédition lancée vers ${data.total} destinataire(s)${data.skipped ? ` (${data.skipped} exclu(s) : désinscrits/e-mails refusés)` : ""} — suivez la barre de progression sous les boutons.`, { duration: 7000 });
      onSent();
    } catch (e) { toast.error(apiErr(e)); setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/70 p-4 aca-modal" data-testid="amb-mail-all-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-white/10 bg-elevated p-6 sm:p-8">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-xl font-700 text-white">{meta.label}</h3>
          <button onClick={() => setState(null)} data-testid="amb-mail-all-close" className="text-ink-secondary hover:text-white"><X size={20} /></button>
        </div>

        {state.step === 1 ? (
          <div className="space-y-4">
            <p className="text-xs text-ink-secondary">
              Le mail <span className="font-600 text-cyan">déjà programmé</span> ci-dessous sera envoyé à tous les ambassadeurs,
              <span className="text-white"> personnalisé pour chacun</span> (nom, code confidentiel, lien d'invitation).
              Pour le modifier : bouton « Mails » de cette page.
            </p>

            {/* Aperçu du mail programmé (logo ACA en haut au centre, comme l'e-mail réel) */}
            <div className="overflow-hidden rounded-xl border border-white/10 bg-white" data-testid="amb-mail-all-preview">
              <div className="flex justify-center bg-[#0a0e1a] px-6 py-5">
                <img src="/email-logo.png" alt="APP CHALLENGE ACADEMY" className="h-11" />
              </div>
              <div className="px-6 py-5">
                <div className="border-b border-gray-200 pb-3 text-[15px] font-700 text-gray-900" data-testid="amb-mail-all-subject">
                  {tpl ? previewSubject : "Chargement du modèle…"}
                </div>
                <div className="whitespace-pre-line pt-4 text-sm leading-relaxed text-gray-700" data-testid="amb-mail-all-body">
                  {tpl ? preview : ""}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-cyan/25 bg-cyan/5 p-4 text-xs leading-relaxed text-ink-secondary" data-testid="amb-mail-all-legal">
              <span className="font-600 text-cyan">Conformité réglementation :</span> chaque envoi est individuel et suivi
              (ouverture / confirmation de lecture). Le pied de page ajoute automatiquement l'identité de l'expéditeur
              et un <span className="text-white">lien de désinscription</span>. Les adresses désinscrites ou déjà
              refusées sont <span className="text-white">exclues automatiquement</span> ; un e-mail refusé par le
              serveur est noté « E-mail refusé » sur la fiche du client.
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setState({ ...state, step: 2 })} disabled={!tpl} data-testid="amb-mail-all-next" className="aca-btn-primary flex-1">
                Continuer
              </button>
              <button onClick={() => setState(null)} data-testid="amb-mail-all-cancel" className="aca-btn-secondary flex-1">
                Annuler
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-200" data-testid="amb-mail-all-confirm">
              Confirmez-vous l'envoi de « <span className="font-600">{previewSubject}</span> » à <span className="font-600">tous les ambassadeurs</span> (inscrits et invités actifs, hors désinscrits et adresses incorrectes) ?
            </div>
            <div className="flex gap-3">
              <button onClick={send} disabled={sending} data-testid="amb-mail-all-send" className="aca-btn-primary flex-1">
                {sending ? "Lancement…" : "Oui, envoyer à tous"}
              </button>
              <button onClick={() => setState({ ...state, step: 1 })} disabled={sending} data-testid="amb-mail-all-back" className="aca-btn-secondary flex-1">
                Retour
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
